import { useEffect, useRef, useState } from 'react';

import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';

import { DialogContent } from '../../../components/Dialog/DialogUiRedesign/DialogContent';
import { Loader } from '../../../components/Loader/Loader';
import { CURRENT_PAYMENTS_PROJECT_URL } from '../../../config/constants';
import { useResize } from '../../../hooks/iframe-helpers';
import { useFlag } from '../../../services/launchDarkly';
import { platformContextService } from '../../../services/platformContext/PlatformContextService';
import { EnumOpenDialog } from '../../../stores/ui/types';
import { useStores } from '../../../stores/useStores';
import { PaymentRouterProviderEnum } from '../../../types';
import { formatURL } from '../../../utils/formatURL';
import { devError } from '../../../utils/log';
import type { ShipCardParams } from '../ExternalPayments';

import styles from './ExternalPayments.module.scss';
import { useIFrameParams } from './useIFrameParams';
import { useMessagesListener } from './useMessagesListener';

export const ExternalPaymentsV5 = observer(function ExternalPaymentsV5(props: {
  storeType: 'store' | 'mini-store' | 'subscription';
  isDeposit?: boolean;
  shipping?: ShipCardParams;
}) {
  const { iframeRef } = useResize();
  const { appStore, uiStore, portalStore } = useStores();

  const [payComSessionData, setPayComSessionData] = useState<{
    sessionId: string;
    clientSessionSecretKey: string;
  } | null>(null);
  const [payComFailed, setPayComFailed] = useState(false);
  const hasPendingPayComSessionRequest = useRef(false);
  const [iframeStyle, setIframeStyle] = useState<{ height?: number | string; background?: string }>();
  const [iframeLoading, setIframeLoading] = useState(true);
  const { gameId } = useParams();
  const { platform } = platformContextService.getConfig();

  const paymentRouter = useFlag<PaymentRouterProviderEnum>('payment_router', PaymentRouterProviderEnum.PAY_COM);
  const externalPaymentUrl = CURRENT_PAYMENTS_PROJECT_URL;
  const iframeParams = useIFrameParams(props, payComSessionData);

  useMessagesListener(setIframeStyle, setIframeLoading, props.storeType === 'mini-store');

  useEffect(() => {
    if (
      paymentRouter === PaymentRouterProviderEnum.PAY_COM &&
      appStore.selectedPackage?.packageId &&
      !payComSessionData &&
      !payComFailed
    ) {
      const fetchPayComSession = () => {
        try {
          const sessionData = {
            sessionId: '123',
            clientSessionSecretKey: '123',
          };
          setPayComSessionData(sessionData);
        } catch (error) {
          devError('Failed to create Pay.com payment session', error);
          setPayComFailed(true);
          setIframeLoading(false);
          portalStore.closePortal();
          uiStore.setOpenDialog({
            dialog: EnumOpenDialog.isOpenPurchaseFailedDialog,
            options: {
              type: 'purchase',
              failureDetails: 'Failed to initialize Pay.com payment session',
            },
          });
        } finally {
          hasPendingPayComSessionRequest.current = false;
        }
      };
      void fetchPayComSession();
    }
  }, [
    paymentRouter,
    appStore.selectedPackage?.packageId,
    payComSessionData,
    payComFailed,
    appStore.selectedPackage,
    uiStore,
    portalStore,
    gameId,
    platform,
  ]);

  const isPayComRouter = paymentRouter === PaymentRouterProviderEnum.PAY_COM;
  if (isPayComRouter && payComFailed) {
    return <></>;
  }

  const isPayComRouterReady = payComSessionData?.clientSessionSecretKey && payComSessionData?.sessionId;

  if (!iframeParams || (isPayComRouter && !props.isDeposit && !props.shipping && !isPayComRouterReady)) {
    return <Loader />;
  }

  return (
    <>
      <iframe
        ref={iframeRef}
        allow="payment"
        className={styles.iframe}
        data-loading={iframeLoading}
        data-testid="payments-iframe"
        sandbox="allow-modals allow-scripts allow-forms allow-same-origin allow-top-navigation allow-popups allow-popups-to-escape-sandbox"
        src={`${formatURL(externalPaymentUrl)}?${iframeParams}`}
        style={iframeStyle}
      />
      {iframeLoading && (
        <DialogContent className={styles.loaderWrapper}>
          <Loader customClassName={styles.loader} />
        </DialogContent>
      )}
    </>
  );
});
